import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "../components/site/Reveal";
import { PROJECTS, PROJECT_CATEGORIES } from "../data/site";

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10 pt-36 pb-16" data-testid="projects-header">
        <div className="sfm-grid-bg pointer-events-none absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber">Selected Work</p>
            <h1 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl lg:text-6xl">
              Projects & Gallery
            </h1>
            <p className="mt-5 max-w-2xl text-base text-white/60 sm:text-lg">
              Real shopfronts, signage, interiors and fleets delivered across Ireland. We're adding new case
              studies here as each project is completed.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="mb-10 flex flex-wrap gap-2" data-testid="project-filters">
          {PROJECT_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`rounded-full border px-5 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                filter === c ? "border-amber bg-amber text-void" : "border-white/15 text-white/60 hover:border-amber hover:text-amber"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="grid gap-6 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                data-testid={`project-card-${p.id}`}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-obsidian"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-void/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber backdrop-blur">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  {p.location && (
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/40">
                      <MapPin size={12} className="text-amber" /> {p.location}
                    </div>
                  )}
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-white">{p.title}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-amber">{p.industry}</p>

                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex gap-2">
                      <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-widest text-white/40">Service</dt>
                      <dd className="text-white/70">{p.service}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-widest text-white/40">Outcome</dt>
                      <dd className="text-white/70">{p.outcome}</dd>
                    </div>
                  </dl>

                  {p.gallery && p.gallery.length > 1 && (
                    <div className="mt-5">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-white/40">{p.gallery[1].label}</p>
                      <div className="mt-2 overflow-hidden rounded-xl border border-white/10">
                        <img
                          src={p.gallery[1].src}
                          alt={`${p.title} — ${p.gallery[1].label}`}
                          loading="lazy"
                          decoding="async"
                          data-testid={`project-detail-image-${p.id}`}
                          className="aspect-[16/10] w-full object-cover"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/50">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-16 rounded-2xl border border-amber/25 bg-obsidian p-10 text-center">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
            Want results like these?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-white/60">
            Let's talk about your storefront. We'll share ideas, references and a clear estimate.
          </p>
          <Link
            to="/contact"
            data-testid="projects-cta-btn"
            className="group mt-6 inline-flex items-center gap-2 rounded-full bg-amber px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-void transition-transform hover:scale-[1.03] hover:bg-amber-glow"
          >
            Get a Free Quote <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Projects;
